import axios from "axios";
import { userBaseURL } from "../util/config";
import * as authStorage from "./authStorage";

type SessionPayload = {
  access_token: string;
  refresh_token?: string;
  user?: Record<string, unknown> | null;
  profile_id?: string | number | null;
};

let session: authStorage.AuthSession | null = null;
let profileId: string | null = null;

function ensureSessionLoaded(): void {
  if (!session) {
    session = authStorage.loadSession();
  }
}

function ensureProfileLoaded(): void {
  if (!profileId) {
    profileId = authStorage.loadProfileId();
  }
}

export function loadSession(): authStorage.AuthSession | null {
  session = authStorage.loadSession();
  profileId = authStorage.loadProfileId();
  return session;
}

export function getAccessToken(): string | null {
  ensureSessionLoaded();
  return session?.access_token ?? null;
}

export function getRefreshToken(): string | null {
  ensureSessionLoaded();
  return session?.refresh_token ?? null;
}

export function setSession(data: SessionPayload): void {
  if (!data || !data.access_token) return;

  session = {
    access_token: data.access_token,
    refresh_token: data.refresh_token ?? session?.refresh_token,
    user: data.user ?? session?.user,
  };

  authStorage.saveSession(session);
  if (data?.profile_id != null && String(data.profile_id).trim() !== "") {
    setProfileId(data.profile_id);
  }
}

export function clearSession(): void {
  session = null;
  profileId = null;
  authStorage.clearSession();
}

export function getProfileId(): string | null {
  ensureProfileLoaded();
  return profileId ?? null;
}

export function setProfileId(nextProfileId: string | number | null | undefined): string | null {
  if (nextProfileId == null || String(nextProfileId).trim() === "") {
    profileId = null;
    authStorage.saveProfileId(null);
    return null;
  }

  profileId = String(nextProfileId);
  authStorage.saveProfileId(profileId);
  return profileId;
}

export async function refreshToken(): Promise<string | null> {
  const refresh = getRefreshToken();
  if (!refresh || typeof refresh !== "string" || refresh.trim() === "") {
    clearSession();
    return null;
  }

  try {
    const response = await axios.post(`${userBaseURL}/auth/token/refresh`, { refresh });
    if (response?.status === 200 && response?.data?.access_token) {
      setSession(response.data);
      return response.data.access_token;
    }
  } catch {
    // ignored - handled by clearing session below
  }

  clearSession();
  return null;
}
